export const MAX_FILE_MB = 100
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export const WATERMARK_POSITIONS = ['center', 'top-left', 'bottom-right', 'diagonal'] as const
export type WatermarkPosition = (typeof WATERMARK_POSITIONS)[number]

export const WATERMARK_ANGLES = [-45, 0, 45] as const
export type WatermarkAngle = (typeof WATERMARK_ANGLES)[number]

export interface WatermarkOptions {
  text: string
  opacity: number
  position: WatermarkPosition
  angle: WatermarkAngle
}

export interface WatermarkResult {
  blob: Blob
  totalPages: number
}

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function watermarkedFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-watermark.pdf`
}

export function angleLabel(angle: WatermarkAngle): string {
  if (angle === 0) return '0°'
  return `${angle > 0 ? '+' : ''}${angle}°`
}

function canvasToPngBytes(canvas: HTMLCanvasElement): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('canvas_to_blob_failed'))
        return
      }
      blob.arrayBuffer().then(resolve).catch(reject)
    }, 'image/png')
  })
}

async function createWatermarkPng(text: string): Promise<{ bytes: ArrayBuffer; width: number; height: number }> {
  const measureCanvas = document.createElement('canvas')
  const measureCtx = measureCanvas.getContext('2d')

  if (!measureCtx) {
    throw new Error('canvas_unavailable')
  }

  const fontSize = 72
  const paddingX = 40
  const paddingY = 28
  const font = `700 ${fontSize}px Arial, Helvetica, sans-serif`

  measureCtx.font = font
  const measuredWidth = Math.max(1, Math.ceil(measureCtx.measureText(text).width))
  const canvas = document.createElement('canvas')
  canvas.width = measuredWidth + paddingX * 2
  canvas.height = fontSize + paddingY * 2

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('canvas_unavailable')
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.font = font
  ctx.fillStyle = '#31353f'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, canvas.width / 2, canvas.height / 2)

  return {
    bytes: await canvasToPngBytes(canvas),
    width: canvas.width,
    height: canvas.height,
  }
}

function resolvePlacement(
  position: WatermarkPosition,
  pageWidth: number,
  pageHeight: number,
  imageWidth: number,
  imageHeight: number,
): { x: number; y: number; width: number; height: number } {
  const padding = Math.min(pageWidth, pageHeight) * 0.08
  const maxWidthRatio = position === 'diagonal' ? 0.72 : 0.48
  const targetWidth = Math.min(pageWidth * maxWidthRatio, imageWidth)
  const targetHeight = imageHeight * (targetWidth / imageWidth)

  switch (position) {
    case 'top-left':
      return {
        x: padding,
        y: pageHeight - padding - targetHeight,
        width: targetWidth,
        height: targetHeight,
      }
    case 'bottom-right':
      return {
        x: pageWidth - padding - targetWidth,
        y: padding,
        width: targetWidth,
        height: targetHeight,
      }
    case 'center':
    case 'diagonal':
      return {
        x: (pageWidth - targetWidth) / 2,
        y: (pageHeight - targetHeight) / 2,
        width: targetWidth,
        height: targetHeight,
      }
  }
}

export async function addTextWatermark(file: File, options: WatermarkOptions): Promise<WatermarkResult> {
  const { PDFDocument, degrees } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  const pages = pdfDoc.getPages()
  const watermark = await createWatermarkPng(options.text)
  const image = await pdfDoc.embedPng(watermark.bytes)

  for (const page of pages) {
    const { width: pageWidth, height: pageHeight } = page.getSize()
    const placement = resolvePlacement(
      options.position,
      pageWidth,
      pageHeight,
      watermark.width,
      watermark.height,
    )
    const angle = options.position === 'diagonal' && options.angle === 0 ? -45 : options.angle

    page.drawImage(image, {
      ...placement,
      rotate: degrees(angle),
      opacity: options.opacity,
    })
  }

  const out = await pdfDoc.save()
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    totalPages: pages.length,
  }
}
